import type { UIMessage } from "@ai-sdk/react";
import { formatDate } from "@cp/toolkit";
import { type ChatMessageFragment, ChatSessionStatus, type ConversationMessageFragment } from "@qw/graphql";
import { MCP_RESOURCE_ORIGIN } from "@qw/qw-common";
import { parseISO } from "date-fns";

import type {
  ExtendedUIMessage,
  McpResourceLink,
  MessagePart,
  MessagePartToolInvocation,
  ResourceLinkHandler,
} from "./chat.types";
// Turn-identity + normalization primitives moved to `render-identity.ts` (B-1458). `dedupeUiMessages`
// below keys on `getRenderKey`, so it is imported here; the primitives themselves stay re-exported via
// the `chat` barrel (`render-identity` is exported alongside this file), so public imports are stable.
import { getRenderKey } from "./render-identity";

const LEGACY_OBJECT_OBJECT_TEXT = "[object Object]";

/**
 * Whether assistant `reasoning` parts are surfaced in the chat UI. While off,
 * {@link isAssistantMessageContentPart} keeps them out of both the renderer and the
 * message-level reveal cursor, so the answer text does not wait behind an invisible reveal.
 *
 * The renderer (`AssistantReasoningChatSessionMessagePart`) and its reveal sequencing are
 * intact: flipping this to `true` restores the "Reasoning" disclosure, including on messages
 * whose reasoning was persisted while it was off (the server keeps streaming reasoning parts).
 */
export const RENDER_REASONING_PARTS = false;

export const adaptChatMessagesToUIMessages = (messages: ChatMessageFragment[]): ExtendedUIMessage[] =>
  messages.map((message) => ({
    id: message.id,
    // Surfaced additively (brief 011); `id` stays the render identity. Used by the
    // server-authoritative render path to match a live turn against its persisted row.
    externalMessageId: message.externalMessageId,
    parts: JSON.parse(message.parts) as UIMessage["parts"],
    role: message.role as UIMessage["role"],
    status: message.status,
    // Note: content will soon be deprecated in @ai-sdk/react
    content: "",
    createdAt: message.createdAt,
  }));

/**
 * Adapts server-assembled `conversationMessages` tree nodes into the render shape
 * (brief 011 PR 4/5; brief 014 Phase 1). This is the `serverHistory` base for the
 * ephemeral-slot render path — already ordered and child-`user`-filtered server-side.
 *
 * `id` is the **`renderKey`** — the server's opaque, globally-unique, session-scoped
 * composite (`base64url([sourceSessionId, externalMessageId])`), non-null on this tree
 * query. It is the correct cross-tree render/React identity: a bare `externalMessageId`
 * is unique only within a session and collides across the merged root+child list (Gate 0).
 * `externalMessageId` is still surfaced additively because it is the slot's drop-predicate
 * key — `canDropTurnFromSlot` matches a live turn's stream id against it, NOT against `id`,
 * so this identity switch does not disturb the live↔persisted handoff. Switching `id` from
 * the DB PK to `renderKey` is render-parity today (both are globally unique); it exists so a
 * live turn tagged with its `sourceSessionId` can later collapse into its persisted row by
 * `renderKey` (brief 014 Phase 1.5/2).
 */
export const adaptConversationMessagesToUIMessages = (messages: ConversationMessageFragment[]): ExtendedUIMessage[] =>
  messages.map((message) => ({
    id: message.renderKey,
    externalMessageId: message.externalMessageId,
    // Surfaced as its own field (not only as `id`) so live and persisted copies key/dedupe on
    // renderKey uniformly (B-1445); a live turn derives the same value via `deriveLiveTurnRenderKey`.
    renderKey: message.renderKey,
    // `parts` is a typed JSON value from `conversationMessages` (SDL `JSON!`), already an
    // object — used directly, not `JSON.parse`d (plan §PR 4: no stringified-parts guessing).
    parts: message.parts as UIMessage["parts"],
    role: message.role as UIMessage["role"],
    status: message.status,
    content: "",
    createdAt: message.createdAt,
  }));

const EMPTY_GENERATED_ARTIFACTS: McpResourceLink[] = [];

const sameResourceLinkList = (left: McpResourceLink[], right: McpResourceLink[]): boolean => {
  if (left === right) {
    return true;
  }
  if (left.length !== right.length) {
    return false;
  }
  for (let i = 0; i < left.length; i++) {
    if (left[i] !== right[i]) {
      return false;
    }
  }
  return true;
};

export const getMcpResourceType = (uri: string): string | undefined => uri.split(".")[1] || undefined;

export const getResourceLinkHandler = (
  resource: Pick<McpResourceLink, "uri">,
  resourceLinkHandlers?: Record<string, ResourceLinkHandler>
): ResourceLinkHandler | undefined => {
  const resourceType = getMcpResourceType(resource.uri);
  return resourceType ? resourceLinkHandlers?.[resourceType] : undefined;
};

export const getResourceLinkTitle = (resource: Pick<McpResourceLink, "name" | "title" | "uri">): string =>
  resource.title || resource.name || resource.uri.split(".").pop() || resource.uri;

export const isResourceLink = (item: unknown): item is McpResourceLink => {
  if (item === null || typeof item !== "object") {
    return false;
  }
  const record = item as Record<string, unknown>;
  return record.type === "resource_link" && typeof record.uri === "string";
};

const isGeneratedResourceLink = (link: McpResourceLink): boolean => {
  const meta = link._meta;
  if (meta === null || typeof meta !== "object") {
    return false;
  }
  return (meta as Record<string, unknown>).origin === MCP_RESOURCE_ORIGIN.Generated;
};

const forEachResourceLinkInMessages = (messages: ExtendedUIMessage[], visit: (link: McpResourceLink) => void): void => {
  for (const message of messages) {
    if (!message.parts) {
      continue;
    }

    for (const part of message.parts) {
      if (!isToolMessagePart(part)) {
        continue;
      }

      const output = part.output;
      if (!output) {
        continue;
      }

      const parsedOutput = typeof output === "string" ? safeJsonParse(output) : output;
      if (!parsedOutput) {
        continue;
      }

      const content = (parsedOutput as Record<string, unknown>).content;
      if (!Array.isArray(content)) {
        continue;
      }

      for (const item of content) {
        if (isResourceLink(item)) {
          visit(item);
        }
      }
    }
  }
};

const dedupeResourceLinksByUri = (links: McpResourceLink[]): McpResourceLink[] => {
  const byUri = new Map<string, McpResourceLink>();
  for (const link of links) {
    if (!byUri.has(link.uri)) {
      byUri.set(link.uri, link);
    }
  }
  return [...byUri.values()];
};

/**
 * Extracts all resource link URIs from a list of messages.
 * Used to seed the "seen" resource URIs atom when the chat provider mounts,
 * ensuring historical resources don't trigger onFirstSeen.
 */
export const extractResourceUrisFromMessages = (messages: ExtendedUIMessage[]): Set<string> => {
  const uris = new Set<string>();
  forEachResourceLinkInMessages(messages, (link) => {
    uris.add(link.uri);
  });
  return uris;
};

/**
 * Absent origin is discovered. Deduped by `uri` so a draft that appears on both the
 * persisted and live copies of a turn is listed once.
 */
export const extractGeneratedArtifactsFromMessages = (messages: ExtendedUIMessage[]): McpResourceLink[] => {
  const generated: McpResourceLink[] = [];
  forEachResourceLinkInMessages(messages, (link) => {
    if (isGeneratedResourceLink(link)) {
      generated.push(link);
    }
  });
  if (generated.length === 0) {
    return EMPTY_GENERATED_ARTIFACTS;
  }
  if (generated.length === 1) {
    return generated;
  }
  return dedupeResourceLinksByUri(generated);
};

/**
 * Per-instance extractor that re-walks only the streaming tail.
 *
 * `useChat().messages` is a fresh array ~33×/sec (`STREAM_THROTTLE_MS = 30`). Memoizing on
 * array identity would re-walk the whole transcript every tick. Settled messages (everything
 * but the last) are walked when the settled count changes; the tail is always walked.
 * Merged results reuse the previous array when the link objects are unchanged so the
 * drawer does not re-render on every throttle tick.
 */
export const createGeneratedArtifactsExtractor = (): ((messages: ExtendedUIMessage[]) => McpResourceLink[]) => {
  let settledCount = -1;
  let settledArtifacts: McpResourceLink[] = EMPTY_GENERATED_ARTIFACTS;
  let lastResult: McpResourceLink[] = EMPTY_GENERATED_ARTIFACTS;

  return (messages: ExtendedUIMessage[]): McpResourceLink[] => {
    const nextSettledCount = Math.max(0, messages.length - 1);
    if (nextSettledCount !== settledCount) {
      settledArtifacts = extractGeneratedArtifactsFromMessages(messages.slice(0, nextSettledCount));
      settledCount = nextSettledCount;
    }

    const tailArtifacts =
      messages.length === 0
        ? EMPTY_GENERATED_ARTIFACTS
        : extractGeneratedArtifactsFromMessages([messages[messages.length - 1]]);

    const nextResult =
      tailArtifacts.length === 0 ? settledArtifacts : dedupeResourceLinksByUri([...settledArtifacts, ...tailArtifacts]);

    if (sameResourceLinkList(nextResult, lastResult)) {
      return lastResult;
    }

    lastResult = nextResult;
    return nextResult;
  };
};

const safeJsonParse = (json: string): unknown => {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const formatChatSessionTitle = (title: string) => {
  try {
    return formatDate(parseISO(title.split(" ")[1]), "MMM d, yyyy hh:mm:ss aa");
  } catch {
    return title;
  }
};

export const hasContentMessagePartTypes = (part: MessagePart) => {
  if (part.type === "step-start") {
    return false;
  }

  if (part.type === "text" || part.type === "reasoning") {
    return typeof part.text === "string" && !!part.text.trim();
  }

  return (
    part.type === "source-url" || part.type === "source-document" || part.type === "file" || isToolMessagePart(part)
  );
};

/**
 * The single visibility policy for assistant message parts. Keeping this separate from
 * {@link hasContentMessagePartTypes} preserves that helper's wire-content semantics while
 * giving the message renderer and its loading placeholder the same definition of visible
 * assistant content.
 */
export const isAssistantMessageContentPart = (part: MessagePart): boolean => {
  return hasContentMessagePartTypes(part) && (part.type !== "reasoning" || RENDER_REASONING_PARTS);
};

export const isToolMessagePart = (part: MessagePart): part is MessagePartToolInvocation => {
  return typeof part.type === "string" && (part.type.startsWith("tool-") || part.type === "dynamic-tool");
};

export const getToolMessagePartName = (part: MessagePartToolInvocation): string => {
  return part.type === "dynamic-tool" ? part.toolName : part.type.replace("tool-", "");
};

export const isLegacyObjectObjectTextPart = (part: MessagePart): boolean => {
  return part.type === "text" && typeof part.text === "string" && part.text.trim() === LEGACY_OBJECT_OBJECT_TEXT;
};

export const getToolCallIds = (parts: UIMessage["parts"] | undefined): string[] => {
  if (!parts) {
    return [];
  }
  const toolCallIds: string[] = [];
  for (const part of parts) {
    const toolCallId = (part as { toolCallId?: unknown }).toolCallId;
    if (isToolMessagePart(part) && typeof toolCallId === "string") {
      toolCallIds.push(toolCallId);
    }
  }
  return toolCallIds;
};

/**
 * Collapse duplicate renders of the same assistant turn, keeping the first
 * occurrence.
 *
 * The Vercel AI SDK reconciles a resumed/replayed stream only against the last
 * message (it appends otherwise — see `useChat`'s `replaceLastMessage` check).
 * On a mid-stream refresh the loaded history already contains the in-flight
 * turn, and child-playbook messages merged in by `createdAt` push it off the
 * tail, so the replayed copy can't reconcile and gets appended as a duplicate;
 * concurrent resume triggers can append several. This collapses those repeats
 * at the render boundary using two signals:
 *   - a shared message id (also prevents duplicate React keys), and
 *   - a shared tool-call id, which stays stable across the persisted and
 *     replayed copies of a turn even when their message ids differ (DB primary
 *     key vs stream-assigned id) and their parts differ (the replay accretes
 *     child content under the same message).
 *
 * Keeping the first occurrence preserves the loaded/persisted copy and its
 * position over the later streamed duplicate. Turns with neither a shared
 * renderKey nor a shared tool-call id are never collapsed.
 *
 * The identity signal is `getRenderKey` (renderKey, else `id`) — NOT the bare `id`
 * (B-1445). On the server-authoritative path a live turn and its persisted row have
 * different ids (the `aisdk-` stream id vs the DB PK) but the SAME renderKey, so keying
 * on renderKey folds the live copy into its persisted row (base-first ⇒ persisted wins)
 * instead of rendering both. Where renderKey is absent (legacy path) `getRenderKey` falls
 * back to `id`, so that path is byte-identical.
 */
export const dedupeUiMessages = <T extends { id: string; renderKey?: string | null; parts?: UIMessage["parts"] }>(
  messages: T[]
): T[] => {
  const seenKeys = new Set<string>();
  const seenToolCallIds = new Set<string>();
  const result: T[] = [];

  for (const message of messages) {
    const toolCallIds = getToolCallIds(message.parts);
    const renderKey = getRenderKey(message);
    const isDuplicate = seenKeys.has(renderKey) || toolCallIds.some((id) => seenToolCallIds.has(id));
    if (isDuplicate) {
      continue;
    }
    seenKeys.add(renderKey);
    for (const toolCallId of toolCallIds) {
      seenToolCallIds.add(toolCallId);
    }
    result.push(message);
  }

  return result;
};

/**
 * Statuses where the server is mid-work AND the user has no other
 * actionable surface — so the input is locked and an "Alby is
 * thinking..." indicator is shown to prevent typing into a 409 from
 * `tryClaim`'s admission. See `chat-form-provider` and
 * `chat-form-text-input` for the consumers.
 *
 * **`AWAITING_USER_INPUT` and `AWAITING_CHILD_PLAYBOOK` are
 * deliberately NOT in this set.** Both are the steady states for an
 * Alby chat with an active elicit (the chat session sits in
 * `AWAITING_CHILD_PLAYBOOK` while a child playbook eliciting input
 * sits in `AWAITING_USER_INPUT`). Locking the composer in either
 * state freezes the chat for the user — they can't type free-form
 * context alongside the elicit form's Skip/Confirm/Deny buttons. The
 * existing 409 risk on typed messages during these states
 * (user-visible error: "A chat response is already in progress for
 * this session") is preferable to freezing the composer. The 409 is
 * recoverable: each of these events starts the recovery path.
 * Confirm/Deny/Skip and child playbook return (complete or error) both
 * resume orchestration and transition the session through `STREAMING`
 * before becoming admissible, so a typed retry in that window will keep
 * 409ing until the resumed turn settles. Only cancel via Stop
 * (`ABORTED`) releases the lock immediately. Restoring type-to-answer-
 * elicit on the backend was attempted in #12852 and closed; the proper
 * fix needs raw-SQL atomic prior-status capture + claimId-scoped lease-
 * CAS widening, which is separate work.
 *
 * `STREAMING` is excluded because the AI SDK status (`isStreaming`)
 * already covers it at the consumer; including it here would double-
 * gate without changing behavior.
 *
 * Net: this set only fires while a recovery path is taking ownership
 * (`RESUMING`). The admission-and-handoff window is now covered by the
 * AI SDK's submitting state — admission doesn't write a session status
 * for it.
 */
const SESSION_BUSY_STATUSES: ReadonlySet<ChatSessionStatus> = new Set([ChatSessionStatus.Resuming]);

export const isChatSessionBusy = (status: ChatSessionStatus | null | undefined): boolean => {
  return status != null && SESSION_BUSY_STATUSES.has(status);
};

/**
 * Busy-indicator copy. `RESUMING` is an interruption the recovery system is
 * actively trying to recover from (not an error yet) — say so honestly rather
 * than the generic "thinking" copy. If recovery gives up, the run terminalizes
 * and an `interrupted` notice message replaces this transient signal.
 */
export const getChatSessionBusyLabel = (status: ChatSessionStatus | null | undefined): string => {
  return status === ChatSessionStatus.Resuming ? "Interrupted — attempting to resume…" : "Alby is thinking...";
};
